<script lang="ts">
	import { tick } from 'svelte';
	import CollapseDown from '../../icons/CollapseDown.svelte';
	import ClickOutside from '@components/ClickOutside.svelte';
	import Icon from '@components/Icon.svelte';
	import Checkmark from '@icons/Checkmark.svelte';
	export let selectedOptions: (string | number | boolean)[] = [];
	export let options: Option[] = [];
	export let label: string;
	export let id: string;
	export let required: boolean = false;
	export let requiredMessage: string | undefined = undefined;

	let className = '';
	export { className as class };
	let isOpen = false;
	let activeIndex = -1;
	let selectElement: HTMLSelectElement;

	const initialValidityState = {
		badInput: false,
		customError: false,
		patternMismatch: false,
		rangeOverflow: false,
		rangeUnderflow: false,
		stepMismatch: false,
		tooLong: false,
		tooShort: false,
		typeMismatch: false,
		valid: true,
		valueMissing: false
	};

	let validityState = initialValidityState;

	interface Option {
		value: string | number | boolean;
		label: string;
	}

	function toggleDropdown() {
		isOpen = !isOpen;
		if (isOpen) {
			activeIndex = options.findIndex((option) => selectedOptions.includes(option.label)) || 0;
		}
	}

	async function toggleOption(option: Option) {
		if (selectedOptions.includes(option.value)) {
			selectedOptions = selectedOptions.filter((selectedOption) => selectedOption !== option.value);
		} else {
			selectedOptions = [...selectedOptions, option.value];
		}

		// activeIndex = -1;
		await tick();
		checkValidity();
	}

	function handleKeyDown(event: KeyboardEvent) {
		switch (event.key) {
			case 'ArrowUp':
				event.preventDefault();
				if (activeIndex > 0) {
					activeIndex--;
					setTimeout(() => scrollIntoView(), 0);
				} else {
					activeIndex = options.length - 1;
				}

				break;
			case 'ArrowDown':
				event.preventDefault();
				if (activeIndex < options.length - 1) {
					activeIndex++;
					setTimeout(() => scrollIntoView(), 0);
				} else {
					activeIndex = 0;
					setTimeout(() => scrollIntoView(), 0);
				}
				if (!isOpen) isOpen = true;
				break;
			case ' ':
				event.preventDefault();
				if (isOpen && activeIndex !== -1) {
					toggleOption(options[activeIndex]);
				} else {
					toggleDropdown();
				}
				break;
			case 'Enter':
				event.preventDefault();
				if (isOpen && activeIndex !== -1) {
					toggleOption(options[activeIndex]);
				} else {
					toggleDropdown();
				}
				break;

			case 'Escape':
				event.preventDefault();
				isOpen = false;
				activeIndex = -1;
				break;
			case 'Tab':
				isOpen = false;
				activeIndex = -1;
				break;
		}
	}

	function scrollIntoView() {
		const optionElement = document.querySelector('.dropdown-option.active');
		if (optionElement) {
			optionElement.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
				inline: 'start'
			});
		}
	}

	function handleBlur() {
		checkValidity();
	}

	const checkValidity = () => {
		if (selectElement?.checkValidity()) {
			validityState = initialValidityState;
		}
	};

	const handleInvalid = (e: Event, preventDefaultErrorTooltips = true) => {
		if (preventDefaultErrorTooltips) {
			e.preventDefault();
		}

		if (e.target instanceof HTMLSelectElement) {
			const {
				badInput,
				customError,
				patternMismatch,
				rangeOverflow,
				rangeUnderflow,
				stepMismatch,
				tooLong,
				tooShort,
				typeMismatch,
				valid,
				valueMissing
			} = e.target.validity;

			// validity is a class instance therefore can't be copied with spread operator
			validityState = {
				badInput,
				customError,
				patternMismatch,
				rangeOverflow,
				rangeUnderflow,
				stepMismatch,
				tooLong,
				tooShort,
				typeMismatch,
				valid,
				valueMissing
			};
		}
	};

	$: shouldShowInvalidMessage = () => !validityState.valid && !validityState.valueMissing;
	$: shouldShowRequiredMessage = () => validityState.valueMissing;
</script>

<ClickOutside on:click={() => (isOpen = false)} class="flex-grow {className}">
	<div
		class="dropdown-wrapper"
		class:has-error={shouldShowInvalidMessage() || shouldShowRequiredMessage()}
	>
		<select
			id="dropdown-{id}-select"
			class="hidden"
			bind:value={selectedOptions}
			on:invalid={handleInvalid}
			bind:this={selectElement}
			multiple
			{required}
		>
			{#each options as option}
				<option value={option.value}>{option.label}</option>
			{/each}
		</select>

		<label
			class={`dropdown-label  ${selectedOptions.length ? 'float-top' : ''}`}
			for="dropdown-{id}-select">{label}</label
		>
		<button
			id="dropdown-{id}"
			type="button"
			class="dropdown-input"
			aria-haspopup="listbox"
			aria-expanded={isOpen}
			aria-label={label}
			role="combobox"
			aria-controls="dropdown-{id}-menu"
			aria-activedescendant={activeIndex !== -1 ? `option-${id}-${activeIndex}` : undefined}
			on:click|preventDefault={toggleDropdown}
			on:blur={handleBlur}
			on:keydown={handleKeyDown}
		>
			{selectedOptions.length ? `Ausgewählt (${selectedOptions.length})` : ''}

			<span
				class="block absolute top-4 right-2.75 transition-transform {isOpen ? '-rotate-180' : ''}"
			>
				<Icon component={CollapseDown} class="!w-5.25 !h-5.25" />
			</span>
		</button>
		{#if isOpen}
			<ul id="dropdown-{id}-menu" class="dropdown-menu" role="listbox" aria-labelledby="dropdown">
				{#each options as option, i (option.value)}
					<li
						class="dropdown-option {i === activeIndex && 'active'}"
						tabindex={i === activeIndex ? 0 : -1}
						role="option"
						aria-selected={selectedOptions.includes(option.label)}
						id="option-{id}-{i}"
						on:click={() => toggleOption(option)}
						on:keydown={handleKeyDown}
					>
						<span
							class="text-primary box-border mr-3 dark:peer-focus:bg-gray-2 dark:peer-hover:bg-gray-2 peer-focus:bg-gray-11 peer-hover:bg-gray-11 bg-white dark:bg-gray-1 dark:border-gray-7 border rounded border-gray-4 w-6 h-6 flex flex-shrink-0 justify-center items-center"
						>
							{#if selectedOptions.includes(option.value)}
								<Icon component={Checkmark} class="!w-4 !h-4 text-magenta" />
							{/if}
						</span>
						{option.label}
					</li>
				{/each}
			</ul>
		{/if}
	</div>

	{#if shouldShowRequiredMessage()}
		<p class="mt-1.5 text-function-red text-additional font-bold">
			{requiredMessage}
		</p>
	{/if}
</ClickOutside>

<style lang="postcss">
	.dropdown-outer-wrapper.has-error {
		@apply border-function-red bg-function-red-bg;
	}

	.dropdown-outer-wrapper.has-error.dropdown-label {
		@apply text-function-red;
	}

	.dropdown-outer-wrapper.has-error.dropdown-input {
		@apply text-function-red focus:text-gray-1 focus:bg-function-red-bg hover:bg-function-red-bg;
	}

	.dropdown-wrapper {
		@apply box-border
      min-w-[12.5rem] 
      w-full
      h-13.5
      align-top
      inline-block
      transition-all
      ease-in-out
      duration-200
      relative
      bg-white
      dark:bg-gray-2
      dark:focus-within:bg-gray-3
      dark:hover:bg-gray-3
      border
      border-gray-4
      dark:border-gray-7
      focus-within:bg-gray-11
      hover:bg-gray-11
      rounded;
	}

	.dropdown-label {
		@apply absolute
      pointer-events-none
      transition-all
      ease-in-out
      duration-200
      h-full
      bg-transparent
      leading-[3.375rem]
      pl-3
      text-gray-4
      dark:text-gray-9
      text-lg;
	}

	.dropdown-label.float-top {
		@apply text-[0.9375rem] leading-8;
	}

	.dropdown-input {
		@apply font-bold
			focus:outline-none
			outline-none
			focus:ring-0
			text-lg
			text-gray-1
			dark:text-white
			h-full
			w-full
			text-left
			px-3
			pt-5.25
			bg-transparent
			rounded;
	}

	.dropdown-menu {
		@apply absolute mt-0.75 border border-gray-2 rounded top-full left-0 right-0 z-10 bg-white p-0 max-h-[15.75rem] overflow-y-scroll;
	}

	.dropdown-option {
		@apply px-2 py-1.5 cursor-pointer text-copy flex flex-row;
	}

	.dropdown-option:hover {
		@apply bg-gray-11;
	}

	.dropdown-option:focus {
		@apply bg-gray-11;
	}

	.dropdown-option.active {
		@apply bg-gray-11;
	}
</style>
